import React, { useState, useMemo, useEffect, useRef } from "react";
import { Ontology, Type } from "../annotation/ontology";
import matcher from "../utils/regexMatcher";
import { Entity } from "../model/entities";
import { Situation as SituationIntermediate } from "../structure/intermediates";
import { crossSVG } from "../constants";

type InputDropdownProps = {
	attribute: string;
	dropDownElements: Entity[] | SituationIntermediate[] | Ontology<Type> | string[];
	dropDownType: "options" | "entity" | "situation" | "ontology" ,
	dropDownMap?: Map<string,string>;
	mutateReferent:
		| ((
				attribute: string,
				value: string,
				kind?: "entity" | "situation" | "options"
		  ) => void)
		| ((attribute: string, value: string) => void);
	value: string;
	disabled: boolean;
	placeHolder?: string;
}

export default function InputDropdown(
	props: InputDropdownProps
) {
	const [open, setOpen] = useState<boolean>(false);
	const [text, setText] = useState<string>("");
	const [focusedIndex, setFocusedIndex] = useState<number>(0);
	const [listSize, setListSize] = useState<number>(0);

	const inputElement = useRef<HTMLDivElement>(null);
	const dropdownElement = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) inputElement.current?.blur();
	}, [open, focusedIndex]);

	useEffect(() => {
		setFocusedIndex(0);
	}, [listSize]);

	useEffect(() => {
		setText(props.value);
	}, [props.value]);

	const dropDownClickHandler = (
		value: string,
		kind: "entity" | "situation" | "options"
	) => {
		if (!(props.dropDownType === "entity" || props.dropDownType === "situation"))
			setText(value);
		setOpen(false);
	};

	const list = useMemo(() => {
		if (props.dropDownType === "options") {
			const options = props.dropDownElements as string[];
			return options.map((value: string, index) => (
				<div
					className="dropdown-item"
					onClick={() => {
						dropDownClickHandler(value, "options");
					}}
					tabIndex={0}>
					{value}
				</div>
			));
		} else if (props.dropDownType === "ontology") {
			const ontologyTypes = props.dropDownElements as Ontology<Type>;
			const filteredTypes = ontologyTypes.types?.filter((t: Type) => {
				return matcher(text, t.value);
			});
			setListSize(filteredTypes.length);
			return filteredTypes.map((t: Type, index) => {
				return (
					<div
						key={t.uuid.uuidString}
						className={`dropdown-item${
							focusedIndex === index ? " focused" : ""
						}`}
						tabIndex={0}
						onClick={(e: React.MouseEvent) => {
							e.preventDefault();
							dropDownClickHandler(t.value, "options");
							props.mutateReferent(
								props.attribute,
								t.value,
								"options"
							);
						}}>
						{t.value}
					</div>
				);
			});
		} else if (props.dropDownType === "entity") {
			const entities = props.dropDownElements as Entity[];
			const filteredEntities = entities.filter((t: Entity) => {
				if (t.canonicalName !== undefined)
					return matcher(text, t.canonicalName);
			});
			setListSize(filteredEntities.length);
			return filteredEntities.map((entity: Entity, index) => {
				const name =
					entity.canonicalName !== undefined
						? entity.canonicalName
						: "";
				return (
					<div
						key={entity.uuid.uuidString}
						className={`dropdown-item${
							focusedIndex === index ? " focused" : ""
						}`}
						tabIndex={0}
						onClick={(e: React.MouseEvent) => {
							e.preventDefault();
							dropDownClickHandler(name, "entity");
							props.dropDownMap!.set(
								entity.uuid.uuidString,
								entity.canonicalName || ""
							);
							props.mutateReferent(
								props.attribute,
								entity.uuid.uuidString,
								"entity"
							);
						}}>
						{name}
					</div>
				);
			});
		} else if (props.dropDownType === "situation") {
			const situations = props.dropDownElements as SituationIntermediate[];
			const filteredSituations = situations.filter(
				(t: SituationIntermediate) => {
					if (t.canonicalName !== undefined)
						return matcher(text, t.canonicalName);
				}
			);
			setListSize(filteredSituations.length);
			return filteredSituations.map(
				(situation: SituationIntermediate, index) => {
					const name =
						situation.canonicalName !== undefined
							? situation.canonicalName
							: "";
					return (
						<div
							key={situation.uuid.uuidString}
							className={`dropdown-item${
								focusedIndex === index ? " focused" : ""
							}`}
							tabIndex={0}
							onClick={(e: React.MouseEvent) => {
								e.preventDefault();
								dropDownClickHandler(name, "situation");
								props.dropDownMap!.set(
									situation.uuid.uuidString,
									situation.canonicalName || ""
								);
								props.mutateReferent(
									props.attribute,
									situation.uuid.uuidString,
									"options"
								);
							}}>
							{name}
						</div>
					);
				}
			);
		}
	}, [text, focusedIndex, props.dropDownElements]);

	const onKeyDownHandler = (e: React.KeyboardEvent) => {
		e.stopPropagation();
		if (e.key === "ArrowUp") {
			setFocusedIndex((index: number) => {
				if (index === 0) {
					return listSize - 1;
				} else {
					return index - 1;
				}
			});
		} else if (e.key === "ArrowDown") {
			setFocusedIndex((index: number) => {
				if (index === listSize - 1) {
					return 0;
				} else {
					return index + 1;
				}
			});
		} else if (e.key === "Enter") {
			e.preventDefault();
			const element = dropdownElement.current?.children[
				focusedIndex
			] as HTMLElement;
			element?.click();
			(e.target as HTMLInputElement).blur();
			if(props.dropDownType === "entity" || props.dropDownType === "situation") {
				setText("");
			}
		}
	};

	const dropdown = open ? (
		<div
			className="dropdown"
			ref={dropdownElement}
			style={{
				width: inputElement.current?.clientWidth,
				zIndex: 9,
			}}>
			{list}
		</div>
	) : null;

	return (
		<div
			className={`input-dropdown ${props.disabled ? "disabled" : ""}`}
			ref={inputElement}
			onBlur={(e: React.FocusEvent) => {
				if (!e.currentTarget.contains(e.relatedTarget as Node)) {
					setOpen(false);
				}
			}}>
			<div className={`input-field ${props.disabled ? "disabled" : ""}`}>
				<input
					id={`input-dropdown-${props.value}`}
					placeholder={
						props.placeHolder
							? `'${props.placeHolder}' only`
							: undefined
					}
					type="text"
					className="dropdown-field"
					value={text}
					onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
						setText(e.target.value);
					}}
					onKeyDown={onKeyDownHandler}
					onFocus={() => {
						setOpen(true);
					}}
					disabled={props.disabled}
				/>
				{props.value && !props.disabled ? (
					<div
						className={props.disabled ? "disabled" : "close"}
						// disabled={props.disabled}
						onClick={() => {
							props.mutateReferent(props.attribute, "");
						}}>{crossSVG}</div>
				) : undefined}
			</div>
			{dropdown}
		</div>
	);
}
